import React, { useRef, useMemo, useEffect } from 'react';
import { BetaSchemaForm } from '@ant-design/pro-components';
import type { ProFormColumnsType } from '@ant-design/pro-components';
import type { ColProps } from 'antd';
import dayjs from 'dayjs';
import { useMount } from 'ahooks';
import { IconFont } from '../IconFont';

const DATE_FORMAT = 'YYYY-MM-DD';
const INPUT_TYPES = ['text', 'digit', 'password', 'money', 'textarea', 'search'];

export interface StandardFilterField {
  name: string;
  type?: string;
  label?: string;
  placeholder?: string;
  options?: any[];
  span?: number | ColProps;
  width?: number | string;
  defaultValue?: any;
  fieldProps?: any;
  columnProps?: Partial<ProFormColumnsType<any>>;
  fixedRight?: boolean;
}

export interface StandardFilterProps {
  fields: StandardFilterField[];
  initialValues?: Record<string, any>;
  onChange?: (vals: Record<string, any>) => void;
  leftRender?: React.ReactNode;
  rightRender?: React.ReactNode;
  showReset?: boolean;
  resetText?: string;
  labelWidth?: number | 'auto';
  labelAlign?: 'left' | 'right';

  [key: string]: any;
}

export const StandardFilter: React.FC<StandardFilterProps> = ({
  fields,
  initialValues = {},
  onChange,
  leftRender,
  rightRender,
  showReset = false,
  resetText = '重置',
  labelWidth = 80,
  labelAlign = 'right',
  formRef,
  ...restFormProps
}) => {
  const innerFormRef = useRef<any>(null);

  useEffect(() => {
    if (formRef) formRef.current = innerFormRef.current;
  }, [formRef]);

  /** 触发外部回调 */
  const emitChange = () => {
    // 延迟一小会儿执行，确保 Form 内部状态已同步
    setTimeout(() => {
      const values = innerFormRef.current?.getFieldFormatValueObject?.();
      if (values) onChange?.(values);
    }, 0);
  };
  useMount(() => {
    emitChange();
  });
  const handleReset = () => {
    innerFormRef.current?.resetFields();
    emitChange();
  };

  const columns: ProFormColumnsType<any>[] = useMemo(() => {
    const sortedFields = [...fields].sort((a, b) => {
      const aFixed = a.fixedRight || a.type === 'dateRange' ? 1 : 0;
      const bFixed = b.fixedRight || b.type === 'dateRange' ? 1 : 0;
      return aFixed - bFixed;
    });

    return sortedFields.map(f => {
      const fieldType = f.type || 'text';
      const isInputType = INPUT_TYPES.includes(fieldType);
      const span = f.span || 6;
      const colProps: ColProps = {};

      // fixed width 优先
      if (f.width) {
        colProps.flex = 'none';
        colProps.style = {
          width: typeof f.width === 'number' ? `${f.width}px` : f.width,
        };
      } else {
        if (typeof span === 'number') {
          colProps.span = span;
        } else {
          Object.assign(colProps, span);
        }
      }

      // 靠右固定
      if (f.fixedRight || f.type === 'dateRange') {
        colProps.style = {
          ...colProps.style,
          marginLeft: 'auto',
        };
      }

      const column = {
        title: f.label,
        dataIndex: f.name,
        valueType: fieldType as any,
        colProps,
        formItemProps: { style: { marginBottom: 0 } },
        fieldProps: {
          placeholder: f.placeholder,
          allowClear: true,
          options: f.options,
          style: { width: '100%' },
          ...f.fieldProps,
        },
        ...f.columnProps,
      };

      const originalOnChange = column.fieldProps?.onChange;
      column.fieldProps.onChange = (...args: any[]) => {
        const val = args[0];
        originalOnChange?.(...args);

        if (val === undefined || val === null || val === '' || !isInputType) {
          emitChange();
        }
      };

      // 输入类组件：补充失焦和回车逻辑
      if (isInputType) {
        column.fieldProps.onBlur = (e: any) => {
          f.fieldProps?.onBlur?.(e);
          emitChange();
        };
        column.fieldProps.onPressEnter = (e: any) => {
          f.fieldProps?.onPressEnter?.(e);
          emitChange();
        };
      }

      // 日期转换逻辑
      if (fieldType === 'date' || fieldType === 'dateRange') {
        column.transform = val => {
          if (!val) return { [f.name]: undefined };
          if (fieldType === 'date') return { [f.name]: dayjs(val).format(DATE_FORMAT) };
          return { [f.name]: [dayjs(val[0]).format(DATE_FORMAT), dayjs(val[1]).format(DATE_FORMAT)] };
        };
        // column.fieldProps.prefix = <IconFont type="icon-date" className="text-[22px]" />;
        column.fieldProps.suffixIcon = false;
      }

      return column;
    });
  }, [fields]);

  const processedInitialValues = useMemo(() => {
    const res = { ...initialValues };
    fields.forEach(f => {
      const val = res[f.name] ?? f.defaultValue;
      // 先设置默认值（包括 0、false 等 falsy 值）
      if (val !== undefined && val !== null) {
        res[f.name] = val;
      }
      // 然后对日期类型做转换
      if (val !== undefined && val !== null) {
        if (f.type === 'date') {
          res[f.name] = dayjs(val);
        }
        if (f.type === 'dateRange' && Array.isArray(val)) {
          res[f.name] = val.map(v => (v ? dayjs(v) : null));
        }
      }
    });
    return res;
  }, [fields, initialValues]);

  return (
      <div className="w-full flex items-start bg-white rounded-md">
        {leftRender && <div className="mr-[12px] flex-shrink-0">{leftRender}</div>}
        <div className="flex-grow">
          <BetaSchemaForm
              autoFocusFirstInput={false} // 禁用自动聚焦
              layout="horizontal"
              grid={true}
              rowProps={{ gutter: [16, 12] }}
              submitter={false}
              labelWidth={labelWidth}
              labelAlign={labelAlign}
              {...restFormProps}
              formRef={innerFormRef}
              columns={columns}
              initialValues={processedInitialValues}
              onValuesChange={(cv, av) => restFormProps.onValuesChange?.(cv, av)}
          />
        </div>
        {/* <div className="ml-4 flex-shrink-0 flex items-center pt-1">
        <Space>
          {showReset && <Button onClick={handleReset}>{resetText}</Button>}
          {rightRender}
        </Space>
      </div> */}
        {rightRender && <div className="ml-[12px] flex-shrink-0">{rightRender}</div>}
      </div>
  );
};
